import React, { useMemo, useState } from 'react';
import EmptyDataTable from '../EmptyDataTable/EmptyDataTable';
import Order from '../../enums/order.enum';
import { TableProps } from './types/TableProps.type';
import { SortTable } from './types/SortTable.type';
import { sortArray } from './utils/sortArray';
import TableRow from './components/TableRow/TableRow';
import TableHeadCell from './components/TableHeadCell/TableHeadCell';
import { CONNECTION_KEY } from './contrants';

const Table: React.FC<TableProps> = ({
  defaultKeys,
  keyAlign,
  values,
  actionsButtons = [],
  defaultFieldSort,
  actionBtnFilter,
}) => {
  const isSortableTable = Boolean(defaultFieldSort);
  const isAvailableActions = actionsButtons && actionsButtons.length !== 0;
  const defaultSort = defaultFieldSort ? { field: defaultFieldSort, order: Order.ASC } : null;
  const [sort, setSort] = useState<SortTable | null>(defaultSort);

  const changeSort = (field: string | null, sortOption: SortTable) => {
    const isSameField = sortOption.field === field;
    const newOrder = isSameField && sortOption.order === Order.ASC ? Order.DESC : Order.ASC;

    setSort({ field, order: newOrder });
  };

  const listTableHeads = defaultKeys.map((optionKey, index) => (
    <TableHeadCell
      key={optionKey}
      optionKey={optionKey}
      index={index}
      totalKeys={defaultKeys.length}
      isAvailableActions={isAvailableActions}
      alignment={keyAlign?.[optionKey] ? `text-${keyAlign[optionKey]}` : 'text-left'}
      isSortable={isSortableTable}
      sort={sort}
      onChangeSort={(field) => changeSort(field, sort)}
    />
  ));

  const listBody = useMemo(() => {
    const listData = isSortableTable ? sortArray(values, sort) : values;

    return listData.map((value, index) => {
      const record = value;
      const actions = actionBtnFilter ? actionBtnFilter(value, actionsButtons) : actionsButtons;
      const isConnected = record[CONNECTION_KEY] === true;
      const zebraClass = isConnected
        ? (index % 2 === 0 ? 'bg-green-100' : 'bg-green-200')
        : (index % 2 === 0 ? 'bg-white' : 'bg-gray-2');

      return (
        <tr key={`${record?.id}`} className={`shadow-inset-table-top hover:bg-stroke ${zebraClass}`}>
          <TableRow
            record={record}
            defaultKeys={defaultKeys}
            actions={actions}
          />
        </tr>
      )
    })
  }, [sort, values, defaultKeys]);

  return (
    <>
      {
        values.length !== 0 && (
          <div className='c-table h-full overflow-x-auto '>
            <table className="c-table__table w-full">
              <thead className="c-table__thead text-nowrap leading-6 text-white">
                <tr className="c-table__tr-head bg-primary">
                  <>
                    {listTableHeads}
                    {isAvailableActions && (
                      <th className='min-w-[120px] w-[120px] rounded-tr-lg' key='button-actions'>
                        <p className="text-nowrap w-full text-center py-1 text-xs leading-6 pr-5">
                          Edit
                        </p>
                      </th>
                    )}
                  </>
                </tr>
              </thead>
              <tbody className="c-table__tbody font-medium bg-white whitespace-nowrap">
                {listBody}
              </tbody>
            </table>
          </div>
        )
      }
      {values.length === 0 && (
        <div className='c-table__empty-table flex-auto flex justify-center items-center'>
          <EmptyDataTable />
        </div>
      )}
    </>
  );
};

export default Table;
